import type { Platform } from "@easyshot/shared";

export interface VideoEstimateView {
  durationSec: number;
  videoCount: number;
  totalCents: number;
  items: Array<{ platform: Platform; chargeCents: number }>;
}

class QuoteTimeout extends Error {
  constructor() { super("Price calculation timed out. No payment was taken. Please retry the price check."); }
}

/** Read-only price check. A stalled fetch/body must not lock the Pay button forever.
 * Retry at most once, and never retry HTTP/auth/rate-limit errors or generation. */
export async function requestVideoQuote(body: string, signal: AbortSignal, timeoutMs = 15_000): Promise<VideoEstimateView> {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (signal.aborted) throw new DOMException("Canceled", "AbortError");
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancel: () => void = () => {};
    try {
      const deadline = new Promise<never>((_resolve, reject) => {
        cancel = () => { reject(new DOMException("Canceled", "AbortError")); controller.abort(); };
        signal.addEventListener("abort", cancel, { once: true });
        timer = setTimeout(() => { reject(new QuoteTimeout()); controller.abort(); }, timeoutMs);
      });
      return await Promise.race([deadline, (async () => {
        const response = await fetch("/api/video/estimate", {
          method: "POST", headers: { "content-type": "application/json" }, body, signal: controller.signal,
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null) as { error?: string } | null;
          throw new Error(payload?.error ?? "Could not calculate video price. No payment was taken.");
        }
        const value = await response.json() as VideoEstimateView;
        if (!value || !Number.isSafeInteger(value.totalCents) || value.totalCents < 0 ||
          !Number.isSafeInteger(value.videoCount) || value.videoCount < 1 ||
          !Number.isFinite(value.durationSec) || value.durationSec <= 0 || !Array.isArray(value.items)) {
          throw new Error("Invalid price response. No payment was taken. Please retry the price check.");
        }
        return value;
      })()]);
    } catch (reason) {
      if (signal.aborted) throw new DOMException("Canceled", "AbortError");
      if (attempt === 1 || !(reason instanceof QuoteTimeout || reason instanceof TypeError)) throw reason;
    } finally {
      clearTimeout(timer); signal.removeEventListener("abort", cancel);
    }
  }
  throw new Error("Could not calculate video price");
}
